/**
 * TypeScript interfaces for Patient Diagnosis API
 * Matches backend DTOs exactly
 *
 * @author CareX Frontend Team
 */

// ========== Enums ==========

export enum DiagnosisStatus {
  DRAFT = 'DRAFT',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  REVIEWED = 'REVIEWED',
}

export enum RiskLevel {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
}

// ========== Tab DTOs ==========

export interface ExamVitalsDTO {
  id?: string;
  dateOfExam?: string; // ISO date string
  height?: string;
  weight?: string;
  bloodPressure?: string;
  bmi?: number;
  // TB Test Details
  tbTestGivenDate?: string;
  tbTestReadDate?: string;
  tbTestType?: string;
  tbTestResult?: string; // 'Negative' | 'Positive'
  tbResultsMm?: string;
  tbActionTaken?: string;
  chestXrayResults?: string;
  tbStatus?: string;
}

export interface AmbulatoryStatusDTO {
  id?: string;
  ambulatoryStatus?: string; // 'Ambulatory' | 'Nonambulatory' | 'Bedridden'
  statusBasedOn?: string; // 'Physical Condition' | 'Mental Condition' | 'Both'
  bedriddenIllness?: string;
  bedriddenRecoveryFromSurgery?: string;
  bedriddenOther?: string;
  bedriddenDurationDays?: number;
  bedriddenEstimatedEndDate?: string;
  bedriddenPermanentExplain?: string;
}

export interface DiagnosesTreatmentsDTO {
  id?: string;
  // Primary Diagnosis
  primaryDiagnosis?: string;
  primaryCanManage?: string; // 'Yes' | 'No'
  primarySupervisionNeeded?: string;
  // Secondary Diagnosis
  secondaryDiagnosis?: string;
  secondaryCanManage?: string;
  secondarySupervisionNeeded?: string;
  // Cognitive Conditions
  mildCognitiveImpairment?: boolean;
  dementia?: boolean;
  // Contagious/Infectious Disease
  contagiousTreatment?: string;
  contagiousCanManage?: string;
  contagiousSupervisionNeeded?: string;
  // Allergies
  allergiesTreatment?: string;
  allergiesCanManage?: string;
  allergiesSupervisionNeeded?: string;
  // Other Conditions
  otherConditionsTreatment?: string;
  otherConditionsCanManage?: string;
  otherConditionsSupervisionNeeded?: string;
}

export interface PhysicalHealthAssessmentDTO {
  id?: string;
  assessmentData?: Record<string, any>; // Flexible JSON structure
  // Extracted indicators (read-only)
  hasAuditoryImpairment?: boolean;
  hasVisualImpairment?: boolean;
  hasMotorImpairment?: boolean;
  requiresContinuousBedCare?: boolean;
}

export interface MentalConditionAssessmentDTO {
  id?: string;
  assessmentData?: Record<string, any>; // Flexible JSON structure
  // Extracted risk indicators (read-only)
  isConfused?: boolean;
  isAggressive?: boolean;
  isWandering?: boolean;
  isSuicidal?: boolean;
  riskLevel?: RiskLevel;
}

export interface CapacityMedicationAssessmentDTO {
  id?: string;
  assessmentData?: Record<string, any>; // Flexible JSON structure
  // Extracted indicators (read-only)
  canBatheSelf?: boolean;
  canFeedSelf?: boolean;
  canAdministerMedications?: boolean;
  requiresMedicationAssistance?: boolean;
  independenceLevel?: number; // 0-100%
}

export interface HospicePhysicianInfoDTO {
  id?: string;
  receivingHospiceCare?: string; // 'Yes' | 'No'
  hospiceTerminalIllness?: string;
  physicalHealthStatus?: string; // 'Good' | 'Fair' | 'Poor'
  comments?: string;
  physicianNameAddress?: string;
  physicianTelephone?: string;
  physicianLengthOfTimePatient?: string;
  physicianSignature?: string;
  physicianDate?: string;
}

// ========== Main Diagnosis DTO ==========

export interface PatientDiagnosisDTO {
  id: string;
  residentId: string;
  diagnosisDate: string;
  status: DiagnosisStatus;
  completionPercentage?: number;
  completedAt?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  // Tab data (nullable - filled independently)
  examVitals?: ExamVitalsDTO;
  ambulatoryStatus?: AmbulatoryStatusDTO;
  diagnosesTreatments?: DiagnosesTreatmentsDTO;
  physicalHealthAssessment?: PhysicalHealthAssessmentDTO;
  mentalConditionAssessment?: MentalConditionAssessmentDTO;
  capacityMedicationAssessment?: CapacityMedicationAssessmentDTO;
  hospicePhysicianInfo?: HospicePhysicianInfoDTO;
  // Multi-tenancy
  tenantId?: string;
  facilityId?: string;
  clientId?: string;
  // Audit fields
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

// ========== Request DTOs ==========

export interface CreatePatientDiagnosisRequest {
  residentId: string;
  diagnosisDate: string;
  examVitals?: ExamVitalsDTO; // Optional initial data
}

// ========== Response Wrappers ==========

export interface ApiResponse<T> {
  data: T;
  message?: string;
  success?: boolean;
}

export interface ApiError {
  message: string;
  status: number;
  timestamp?: string;
}

// ========== Pagination ==========

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}
